import { FormRows, ModalActionTypes } from '@acorns/web-components'
import * as Sentry from '@sentry/browser'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import { pathOr } from 'ramda'
import { object, string } from 'yup'

import AutoCompleteAddress from 'components/AutocompleteAddress'
import { ActionType, PageName } from 'generated/types'
import { sanitizeAddress } from 'utils/address'
import { sendToSentry, transformErrors } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getGeneralError } from 'utils/getGeneralError'
import states from 'utils/states'
import { phoneMask } from 'utils/text-masks/phone'
import { zipMask } from 'utils/text-masks/zip'
import { noSpecialCharacters } from 'utils/validation-schemas/helpers'

noSpecialCharacters()

export type AddressProps = {
  street1: string
  street2?: string
  city: string
  state: string
  zip: string
  phoneNumber: string
}

// Validation messages
const addressError = 'Please enter a valid address.'
const poBoxError = 'P.O. Box is not supported.'
const cityError = 'Please enter a valid city.'
const stateError = 'Please enter a valid state.'
const zipCodeError = 'Please enter a valid zip code.'
const phoneError = 'Please enter a valid phone number.'
const areaCodeError = 'Please use a valid area code.'
const phoneInternationalError = 'Please enter a US phone number.'

export const validationSchema = object().shape({
  address1: string()
    .required(addressError)
    .matches(
      /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i,
      poBoxError,
    )
    .excludeSpecialCharacters(),
  address2: string()
    .ensure()
    .excludeSpecialCharacters(),
  city: string()
    .required(cityError)
    .excludeSpecialCharacters(),
  state: string().required(stateError),
  zip: string()
    .required(zipCodeError)
    .min(5, zipCodeError)
    .max(5, zipCodeError),
  phoneNumber: string()
    .required(phoneError)
    .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, phoneError)
    .test('sameNumberRepeated', phoneError, value => {
      return value.replace(/[{()\-\s}]/g, '').match(/^(?!(\\d)\\1{8})/)
    })
    .test('areaCodeError', areaCodeError, value => {
      const areaCode = parseInt(value.substring(1, 4), 10)
      return areaCode > 199 && areaCode <= 999
    })
    .test('invalidPhoneNumberCatchAll', phoneInternationalError, value => {
      return parsePhoneNumberFromString(value, 'US')?.isValid()
    }),
})

export const mapPropsToValues = ({ data }) => {
  return sanitizeAddress(data)
}

// Handles Formik's validate event.
export const validate = (values, props) => {
  let errors = {}

  if (props.submitAttempted || props.forGrayOutCTACheck) {
    try {
      validationSchema.validateSync(values, { abortEarly: false })
    } catch (validationErrors) {
      // Convert validation errors to Formik errors collection
      errors = transformErrors(validationErrors.inner)
    }
  }

  return errors
}

export const isInitialValid = props => {
  return validationSchema.isValidSync(mapPropsToValues(props))
}

export const formRows: (validateForm) => FormRows = validateForm => [
  [
    {
      label: 'Address *',
      name: 'address1',
      type: AutoCompleteAddress,
      placeholder: '123 Oak Drive',
      autocomplete: 'off',
      validateForm,
    },
    {
      label: 'Address (Line 2)',
      name: 'address2',
      type: 'text',
      placeholder: '#456',
    },
  ],
  [
    {
      label: 'City *',
      name: 'city',
      type: 'text',
      placeholder: 'Los Angeles',
    },
    {
      label: 'State *',
      name: 'state',
      options: states,
      type: 'select',
    },
  ],
  [
    {
      label: 'Zip Code *',
      mask: zipMask,
      name: 'zip',
      type: 'text',
      placeholder: '90007',
      guide: false,
    },
    {
      label: 'Phone Number *',
      mask: phoneMask,
      name: 'phoneNumber',
      placeholder: '(123) 456-7890',
      type: 'text',
      guide: false,
      helperText:
        'We’ll use this number to protect your account by sending  two-factor authentication verification codes. Standard message rates may apply.',
    },
  ],
]

export const noPOBoxErrorModal = props => ({
  title: 'Error',
  message: 'PO Box is not allowed for an investment account. Please try again!',
  actions: [
    {
      type: ModalActionTypes.primary,
      label: 'Okay',
      handler: props.closeErrorModal,
    },
  ],
})

export const handleSubmit = (values, { props, ...ownProps }) => {
  Sentry.addBreadcrumb({
    message: 'User is attempting to update contact information',
  })
  ownProps.setSubmitting(true)

  const variables = {
    ...values,
  }

  return props
    .updateContactInfo({
      variables,
    })
    .then(() => {
      props.isDynamicFunnelEnabled
        ? props.routeToPage(PageName.ContactInfo, ActionType.Continue)
        : props.route({ from: FlowRoute.ContactInfo, action: 'continue' })
    })
    .catch(error => {
      const errorMessage = pathOr(null, ['graphQLErrors', 0, 'data', 'errors', 0], error)

      if (errorMessage && errorMessage.toLowerCase().includes('address cannot be a po box')) {
        props.showErrorModal(noPOBoxErrorModal(props))
      } else {
        const retryHandler = () => {
          props.closeErrorModal()
          ownProps.submitForm()
        }
        const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
        props.showErrorModal({ title, message, actions, image })
      }

      sendToSentry(error)
      ownProps.setSubmitting(false)
    })
}
